import React from 'react';
import {
    IonButton,
    IonChip,
    IonIcon,
    IonSpinner,
    IonText,
} from '@ionic/react';
import {
    checkmarkCircleOutline,
    closeCircleOutline,
    refreshOutline,
    timeOutline,
    alertCircleOutline,
} from 'ionicons/icons';

export type OrderPublicStatus = 'pending' | 'paid' | 'expired';

export interface StorefrontOrderStatusProps {
    uuid: string;
    status: OrderPublicStatus | null;
    isFetching: boolean;
    isNotFound: boolean;
    errorMessage?: string;
    invoiceDownloadUrl?: string | null;
    onRefresh: () => void;
}

/**
 * Map a public proforma status to an Ionic colour.
 * Mirrors the statusColor mapper in OrderCard but restricted to the
 * three statuses the public endpoint exposes (pending / paid / expired).
 */
export const publicStatusColor = (status: OrderPublicStatus): string => {
    switch (status) {
        case 'paid':    return 'success';
        case 'pending': return 'warning';
        case 'expired': return 'danger';
    }
};

/**
 * Human-readable label for each public status.
 * Mirrors the statusLabel mapper in OrderCard.
 */
export const publicStatusLabel = (status: OrderPublicStatus): string => {
    switch (status) {
        case 'paid':    return 'Paid';
        case 'pending': return 'Pending';
        case 'expired': return 'Expired';
    }
};

const getIcon = (status: OrderPublicStatus | null, errorMessage?: string) => {
    if (status === 'paid') return checkmarkCircleOutline;
    if (status === 'expired' || errorMessage) return closeCircleOutline;
    return timeOutline;
};

const getColor = (status: OrderPublicStatus | null, errorMessage?: string): string => {
    if (status === 'paid') return 'success';
    if (status === 'expired' || errorMessage) return 'danger';
    return 'warning';
};

/**
 * StorefrontOrderStatusComponent — screen 5 of 5, public order-status page.
 *
 * Shows the status of an order by its proforma UUID.
 * No authentication required.
 *
 * SCOPE NOTE (V1): The public API currently exposes only status by UUID —
 * not full line-item detail. This page therefore shows status + reference
 * only. A richer public order-detail view (line items, totals) would require
 * a new or extended public endpoint and is DEFERRED for V1. Guests already
 * receive the full invoice + tickets by email.
 */
export const StorefrontOrderStatusComponent: React.FC<StorefrontOrderStatusProps> = ({
    uuid,
    status,
    isFetching,
    isNotFound,
    errorMessage,
    invoiceDownloadUrl,
    onRefresh,
}) => {
    // --- Not-found state (404 from API) ---
    if (isNotFound) {
        return (
            <div className="ion-padding ion-text-center">
                <div style={{ paddingTop: '40px' }}>
                    <IonIcon
                        icon={alertCircleOutline}
                        color="medium"
                        style={{ fontSize: '64px', marginBottom: '16px' }}
                        data-testid="order-status-not-found-icon"
                    />
                    <IonText color="medium">
                        <h2 data-testid="order-status-not-found-title">Order not found</h2>
                        <p data-testid="order-status-not-found-description">
                            We couldn't find an order matching this reference. Please check your link or contact support.
                        </p>
                    </IonText>
                </div>
            </div>
        );
    }

    // --- Loading state (initial fetch, no status yet) ---
    if (isFetching && status === null) {
        return (
            <div className="ion-padding ion-text-center">
                <div style={{ paddingTop: '40px' }} data-testid="order-status-loading">
                    <IonSpinner name="crescent" />
                    <p>Checking your order…</p>
                </div>
            </div>
        );
    }

    const iconColor = getColor(status, errorMessage);

    return (
        <div className="ion-padding">
            <div className="ion-text-center" style={{ paddingTop: '32px' }}>
                <IonIcon
                    icon={getIcon(status, errorMessage)}
                    color={iconColor}
                    style={{ fontSize: '64px', marginBottom: '16px' }}
                    data-testid="order-status-icon"
                />

                {status && (
                    <div style={{ marginBottom: '12px' }}>
                        <IonChip
                            color={publicStatusColor(status)}
                            data-testid="order-status-chip"
                        >
                            {publicStatusLabel(status)}
                        </IonChip>
                    </div>
                )}

                {errorMessage ? (
                    <IonText color="danger">
                        <h2 data-testid="order-status-title">Unable to load order</h2>
                        <p data-testid="order-status-error">{errorMessage}</p>
                    </IonText>
                ) : (
                    <IonText color={iconColor as any}>
                        <h2 data-testid="order-status-title">
                            {status === 'paid'    && 'Payment Confirmed'}
                            {status === 'expired' && 'Payment Expired'}
                            {status === 'pending' && 'Payment Pending'}
                            {status === null      && 'Checking order…'}
                        </h2>
                    </IonText>
                )}

                <p
                    style={{ color: 'var(--ion-color-medium)', fontSize: '0.9rem', margin: '4px 0 20px' }}
                    data-testid="order-status-reference"
                >
                    Reference: {uuid}
                </p>

                {status === 'paid' && !errorMessage && (
                    <p data-testid="order-status-paid-message">
                        Your order is confirmed. Check your email for your invoice and tickets.
                    </p>
                )}

                {status === 'paid' && invoiceDownloadUrl && (
                    <IonButton
                        expand="block"
                        color="dark"
                        onClick={() => { window.location.href = invoiceDownloadUrl; }}
                    >
                        Download Invoice
                    </IonButton>
                )}

                {status === 'expired' && !errorMessage && (
                    <p data-testid="order-status-expired-message">
                        This payment link has expired. Please contact the school if you believe this is an error.
                    </p>
                )}

                {/* Refresh / check-status affordance */}
                <div className="ion-margin-top">
                    <IonButton
                        fill="outline"
                        onClick={onRefresh}
                        disabled={isFetching}
                        data-testid="order-status-refresh-button"
                    >
                        {isFetching ? (
                            <IonSpinner name="crescent" />
                        ) : (
                            <>
                                <IonIcon icon={refreshOutline} slot="start" />
                                Refresh status
                            </>
                        )}
                    </IonButton>
                </div>
            </div>
        </div>
    );
};

export default StorefrontOrderStatusComponent;
